import React, { useMemo, PropsWithChildren } from 'react';

import { Point, DivIcon } from 'leaflet';
import {
  Marker as ReactLeafletMarker,
  MarkerProps as ReactLeafletMarkerProps,
} from 'react-leaflet';
import clsx from 'clsx';

import {
  useFormattedEstatesCount,
  MAX_ESTATES_COUNT,
} from '../map-marker.hooks';

import { Pointer } from '../types';

import classes from './marker.module.scss';

type MarkerProps = {
  pointer: Pointer;
  className?: string;
  highlighted?: boolean;
  disabled?: boolean;
} & Omit<ReactLeafletMarkerProps, 'position' | 'icon'>;

export const MARKER_SIZE = Object.freeze({
  small: 18,
  medium: 20,
  large: 28,
});

export const Marker = ({
  children,
  pointer,
  className,
  disabled,
  ...markerProps
}: PropsWithChildren<MarkerProps>) => {
  const { estatesCount, formattedEstatesCount, hasMultipleEstates } =
    useFormattedEstatesCount(pointer);

  const icon: any = useMemo(() => {
    const markerSize = !hasMultipleEstates
      ? MARKER_SIZE.small
      : estatesCount === MAX_ESTATES_COUNT
      ? MARKER_SIZE.large
      : MARKER_SIZE.medium;
    return new DivIcon({
      html: `<div class='${clsx(
        classes.marker,
        disabled && classes.disabled,
        !hasMultipleEstates && classes.small,
      )}'><span class='${classes.numberWrapper}'>
        ${formattedEstatesCount}
        </span></div>`,
      iconSize: new Point(markerSize, markerSize),
      iconAnchor: [markerSize / 2, markerSize / 2],
      className: className,
    });
  }, [estatesCount, formattedEstatesCount, disabled]);

  const coordinates = useMemo<[number, number]>(() => {
    return [pointer.point.coordinates[1], pointer.point.coordinates[0]];
  }, [pointer.point.coordinates]);

  return (
    <ReactLeafletMarker icon={icon} position={coordinates} {...markerProps}>
      {children}
    </ReactLeafletMarker>
  );
};
